//
// Copyright (c) 2026 Comcast
//

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-final-form'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import arrayMutators from 'final-form-arrays'

import { usePrevious } from 'utils/hooks'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import Modal from 'components/Shared/Modal/Index'
import ModalHeader from 'components/Shared/Modal/ModalHeader'
import ModalTabs from 'components/Shared/Modal/ModalTabs'
import ModalBody from 'components/Shared/Modal/ModalBody'
import ModalFooter from 'components/Shared/Modal/ModalFooter'

import Button from 'components/Shared/Button/Index'
import TableForm from 'components/Shared/TableForm/Index'

import TableFormFieldsBuilder from 'components/Shared/TableFormFieldsBuilder/Index'
import RequiredLegend from 'components/Shared/TableForm/RequiredLegend'

import { confirm } from 'components/Shared/ConfirmDialog/Index'

const FooterInfoline = styled.div`
  position: absolute;
  top: 16px;
  left: 32px;
  margin: 0 !important;
`

const Spacer = styled.div`
  flex: 1;
`

// if initialTabId exists in tabs, use it, otherwise use first tab or null
const getInitialTabId = (tabs, initialTabId) => {
  const firstTabId = (tabs?.length > 0) ? tabs[0].id : null
  if (initialTabId) {
    const tab = tabs.find(tab => tab.id === initialTabId)
    return tab ? initialTabId : firstTabId
  }
  return firstTabId
}

const ModalFormRenderer = ({
  onBeforeSubmit, onSubmit, initialValues, closeForm, visible, title, children, confirmOnClose,
  confirmOnSubmit, confirmBodyComponent, showJustificationOnConfirm, actionButtonTitle,
  additionalButton, mutators, disableActionButton, fieldParams, loading, formValidator,
  subscribeToValues, tabs, initialTabId, flatFieldNames, userTimeZone,
}) => {
  const formConfigRef = useRef({
    handleSubmit: null,
    pristine: null,
    form: null,
  })
  const [activeTabId, setActiveTabId] = useState(getInitialTabId(tabs, initialTabId))

  const { t } = useTranslation()
  const visiblePrev = usePrevious(visible)

  useEffect(() => {
    const { form } = formConfigRef.current

    // We cannot reset form on submit now as it remains opened for some time or after error,
    // so we do it only when modal disappears.

    // We temporarily change keepDirtyOnReinitialize to be able to reset form on submit
    // See https://github.com/final-form/final-form/issues/151
    if (visiblePrev && !visible) {
      form?.setConfig('keepDirtyOnReinitialize', false)
      form?.reset()
      form?.setConfig('keepDirtyOnReinitialize', true)
    }

    if (!visiblePrev && visible) {
      setActiveTabId(getInitialTabId(tabs, initialTabId))
    }
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible) {
      setActiveTabId(getInitialTabId(tabs, initialTabId))
    }
  }, [visible, tabs, initialTabId])

  const handleModalClose = () => {
    const { pristine } = formConfigRef.current
    if (!pristine && confirmOnClose) {
      confirm({ title: t('common.cancel'), message: t('common.areYouSure') }).then(
        () => {
          closeForm()
        },
        () => {}
      )
    } else {
      closeForm()
    }
  }

  const handleModalSubmit = (event) => {
    const { handleSubmit, form } = formConfigRef.current
    const values = form.getState().values

    if (confirmOnSubmit) {
      confirm({
        title: t('common.confirmChanges'),
        message: t('common.areYouSure'),
        confirmLabel: t('common.confirm'),
        cancelLabel: t('common.cancel'),
        bodyComponent: confirmBodyComponent,
        bodyComponentProps: { values: values, showJustification: showJustificationOnConfirm },
        showJustification: showJustificationOnConfirm,
        noMargins: !!confirmBodyComponent,
      }).then(
        (props) => {
          if (showJustificationOnConfirm && onBeforeSubmit) {
            // make sure that onBeforeSubmit() executes before handleSubmit()
            // asynchronousely allowing outer component to memorize justification
            return Promise.resolve(onBeforeSubmit(props)).then(() => {
              handleSubmit(event)
            })
          } else {
            handleSubmit(event)
          }
        },
        () => {}
      )
    } else {
      handleSubmit(event)
    }
  }

  const onTabChange = (tab) => {
    setActiveTabId(tab.id)
  }

  const actionButtonTitle_ = actionButtonTitle || t('common.submit')

  // 'subscription' is used for performance in input fields and needs to be in sync with used form parameters
  // it looks like adding any boolean to 'subscription' does not degrade performance but absence of it does
  // see https://github.com/final-form/react-final-form-arrays/issues/22#issuecomment-822488262
  // We wanted to use separate initialValue param for each field, but have to use shared
  // initialValues due to https://github.com/final-form/react-final-form/issues/880
  // We need keepDirtyOnReinitialize to correctly handle AsyncSelect fields: without it they will be overriden by init values
  // https://github.com/final-form/react-final-form/issues/246

  // subscribeToValues is required for SettingsModal where form parameters are changed depending on select value
  // without it form.change does not initiate revalidation and Submit button became disabled (invalid form) in this scenario:
  //   1) select some non-null value of provider type (allOf field)
  //   2) select null
  // parameter is added because it may reduce performance when it is true
  return (
    <Form
      initialValues={initialValues}
      mutators={{
        ...mutators,
        ...arrayMutators,
      }}
      onSubmit={values => {
        onSubmit(values)
      }}
      keepDirtyOnReinitialize
      subscription={{ invalid: true, pristine: true, values: !!subscribeToValues }}
      validate={formValidator}
    >
      {({ handleSubmit, pristine, form, invalid }) => {
        formConfigRef.current = { handleSubmit, pristine, form }
        let actionButtonDisabled
        if (disableActionButton !== undefined) {
          actionButtonDisabled = disableActionButton
        } else {
          actionButtonDisabled = pristine || invalid
        }

        return (
          <Modal
            open={visible}
            closeModal={handleModalClose}
            ignoreOverlayClicks={loading}
            alignTop={!!tabs}
            isModalForm
          >
            <LoadingIndicator loading={!!loading} />
            <ModalHeader
              closeModal={handleModalClose}
              withNotification
              withTabs={!!tabs}
            >
              {title}
            </ModalHeader>
            {!!tabs && (
              <ModalTabs
                tabs={tabs}
                activeTabId={activeTabId}
                onTabChange={onTabChange}
              />
            )}
            <ModalBody>
              {!!fieldParams && (
                <TableForm width='100%' noBody>
                  <TableFormFieldsBuilder
                    fieldParams={fieldParams}
                    form={form}
                    isInModal
                    activeTabId={activeTabId}
                    flatFieldNames={flatFieldNames}
                    userTimeZone={userTimeZone}
                  />
                </TableForm>
              )}
              {children}
            </ModalBody>
            <ModalFooter>
              {additionalButton}

              <Spacer />

              <Button
                large
                disabled={actionButtonDisabled}
                onClick={handleModalSubmit}
              >
                {actionButtonTitle_}
              </Button>
              <Button large secondary onClick={handleModalClose}>{t('common.cancel')}</Button>
              <FooterInfoline>
                <RequiredLegend fieldParams={fieldParams} />
              </FooterInfoline>
            </ModalFooter>
          </Modal>
        )
      }}
    </Form>
  )
}

ModalFormRenderer.propTypes = {
  onBeforeSubmit: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  closeForm: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  confirmOnClose: PropTypes.bool,
  confirmOnSubmit: PropTypes.bool,
  confirmBodyComponent: PropTypes.func,
  showJustificationOnConfirm: PropTypes.bool,
  actionButtonTitle: PropTypes.string,
  additionalButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  mutators: PropTypes.object,
  disableActionButton: PropTypes.bool,
  fieldParams: PropTypes.array,
  loading: PropTypes.bool,
  formValidator: PropTypes.func,
  subscribeToValues: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  initialTabId: PropTypes.string,
  flatFieldNames: PropTypes.bool,
  userTimeZone: PropTypes.string,
}

export default ModalFormRenderer
